import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import withRouter from "Components/Common/withRouter"
import FormPageTemplate from "Components/templates/form-page-template"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import DepartmentKPIForm from "Components/organisms/forms/department-kpi-form"
import DepartmentKPIFields from "Components/organisms/forms/department-kpi-form/types"
import { DepartmentKPIFormValidation } from "data/department-kpi-form"
import useAreYouSureModal from "hooks/useAreYouSureModal"
import Department from "models/department"
import DepartmentKPI from "models/department-kpi"
import FiscalYear from "models/fiscal-year"
import Branch from "models/branch"
import { PermissionTypeName } from "models/employee-department-permission"
import {
    getDepartment,
    getDepartmentKPIs,
    getDepartmentKPIFormFields,
    submitEditDepartmentKPIForm
} from "slices/admin/departments/thunk"
import { navigateToDepartmentDetails } from "utils/navigator"

const EditDepartmentKPIPage = (props: any) => {
    document.title = "Edit Department KPIs | IbexIQ";
    const [searchParams] = useSearchParams()
    const departmentId = searchParams.get("departmentId")
    const branchId = searchParams.get("branchId")
    const fiscalYear = searchParams.get("fiscalYear")

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [department, setDepartment] = useState<Department | null>(null)
    const [kpi, setKPI] = useState<DepartmentKPI | null>(null)
    const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([])
    const [branches, setBranches] = useState<Branch[]>([])

    const confirmModal = useAreYouSureModal()

    const initializePage = async () => {
        if (!departmentId || !branchId || !fiscalYear) {
            toast.error("Department ID, branch, and fiscal year are required")
            props.router.navigate(-1)
            return
        }

        const [dept, kpis, fields] = await Promise.all([
            getDepartment(departmentId),
            getDepartmentKPIs(departmentId),
            getDepartmentKPIFormFields()
        ])

        if (!dept) {
            toast.error("Could not find department")
            props.router.navigate(-1)
            return
        }

        // Mirrors the Admin+ check the API enforces on PATCH /departments/:id/kpis/:fiscalYear.
        const canManage = !!dept.isGlobalAdmin
            || dept.yourPermission === PermissionTypeName.Owner
            || dept.yourPermission === PermissionTypeName.Admin

        if (!canManage) {
            toast.error("You do not have permission to manage this department's KPIs")
            props.router.navigate(-1)
            return
        }

        const existing = kpis?.find(k => k.branchId === branchId && k.fiscalYear === Number(fiscalYear))
        if (!existing) {
            toast.error("Could not find KPIs for that branch and fiscal year")
            navigateToDepartmentDetails(props.router.navigate, departmentId)
            return
        }

        setDepartment(dept)
        setKPI(existing)
        if (fields) {
            setFiscalYears(fields.fiscalYears)
            setBranches(fields.branches)
        }

        setIsLoading(false)
    }

    useEffect(() => {
        initializePage()
    }, [departmentId, branchId, fiscalYear])

    const handleBack = () => {
        if (!departmentId) return
        navigateToDepartmentDetails(props.router.navigate, departmentId)
    }

    const onSubmit = async (vals: DepartmentKPIFields) => {
        if (!departmentId || !branchId || !fiscalYear) return

        confirmModal.open({
            title: "Update KPIs",
            subTitle: `Save these ${fiscalYear} KPI changes to ${department?.code}?`,
            acceptText: "Update",
            processingText: "Saving...",
            action: async () => {
                const res = await submitEditDepartmentKPIForm(departmentId, branchId, Number(fiscalYear), vals)
                if (res) {
                    navigateToDepartmentDetails(props.router.navigate, departmentId)
                }
                return res
            }
        })
    }

    const {
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        isValid,
        isSubmitting
    } = useFormik({
        initialValues: {
            branchId: kpi?.branchId ?? '',
            fiscalYear: kpi?.fiscalYear ?? '',
            budget: kpi?.budget ?? '',
            annualRevenue: kpi?.annualRevenue ?? '',
            grossMargin: kpi?.grossMargin ?? '',
            workInHand: kpi?.workInHand ?? '',
            performanceManagement: kpi?.performanceManagement ?? '',
            blueZone: kpi?.blueZone ?? '',
            yellowZone: kpi?.yellowZone ?? '',
            opportunityWinTotal: kpi?.opportunityWinTotal ?? '',
            projectProfitability: kpi?.projectProfitability ?? '',
            arOverdueTarget: kpi?.arOverdueTarget ?? '',
            turnoverTarget: kpi?.turnoverTarget ?? '',
            winRateTarget: kpi?.winRateTarget ?? ''
        } as DepartmentKPIFields,
        onSubmit,
        enableReinitialize: true,
        validationSchema: DepartmentKPIFormValidation
    })

    // Visible years from the API, plus the KPI's own year in case it has since been hidden.
    const fiscalYearOptions = useMemo(() => {
        const years = fiscalYears.map(f => f.id)
        const own = Number(values.fiscalYear)
        if (Number.isInteger(own) && own > 0 && !years.includes(own)) years.push(own)
        return years.sort((a, b) => b - a).map(y => ({ value: String(y), label: String(y) }))
    }, [fiscalYears, values.fiscalYear])

    const branchOptions = useMemo(() => branches.map(b => ({
        value: b.id, label: b.name
    })), [branches])

    return <FormPageTemplate
        title="Admin"
        breadcrumbItem={department ? `Edit KPIs — ${department.code}` : "Edit Department KPIs"}
        isLoading={isLoading}
        loadingTitle="Loading KPIs"
        loadingSubtitle="Fetching department KPI details..."
        form={<DepartmentKPIForm
            edit
            fiscalYears={fiscalYearOptions}
            branches={branchOptions}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleSubmit={handleSubmit}
            handleCancel={handleBack}
            isValid={isValid}
            isSubmitting={isSubmitting}
        />}
        modal={<AreYouSureModal {...confirmModal.modalProps} />}
    />
}

export default withRouter(EditDepartmentKPIPage)
